import React, { useState, useMemo } from 'react';
import { useTranslation } from '../hooks/useTranslation';
import { useBudget } from '../App';

const TransactionList = () => {
  const { t, isRTL } = useTranslation();
  const { transactions, deleteTransaction, categories, currency } = useBudget();
  
  const [filters, setFilters] = useState({
    type: 'all',
    category: 'all',
    month: 'all',
    search: ''
  });

  const [sortBy, setSortBy] = useState('date');
  const [sortOrder, setSortOrder] = useState('desc');

  // Format currency
  const formatCurrency = (amount) => {
    const formattedAmount = new Intl.NumberFormat(isRTL ? 'ar-YE' : 'en-US').format(amount);
    return `${formattedAmount} ${t(`currencies.${currency}`)}`;
  };

  // Filter and sort transactions
  const filteredTransactions = useMemo(() => {
    let filtered = transactions.filter(transaction => {
      // Type filter
      if (filters.type !== 'all' && transaction.type !== filters.type) return false;
      
      // Category filter
      if (filters.category !== 'all' && transaction.category !== filters.category) return false;
      
      // Month filter
      if (filters.month !== 'all') {
        const transactionMonth = transaction.date.substring(0, 7);
        if (transactionMonth !== filters.month) return false;
      }
      
      // Search filter
      if (filters.search) {
        const searchTerm = filters.search.toLowerCase();
        const matchesDescription = transaction.description?.toLowerCase().includes(searchTerm);
        const matchesCategory = t(`categories.${transaction.category}`).toLowerCase().includes(searchTerm);
        if (!matchesDescription && !matchesCategory) return false;
      }
      
      return true;
    });

    // Sort transactions
    filtered.sort((a, b) => {
      let comparison = 0;
      
      switch (sortBy) {
        case 'date':
          comparison = new Date(a.date) - new Date(b.date);
          break;
        case 'amount':
          comparison = parseFloat(a.amount) - parseFloat(b.amount);
          break;
        case 'category':
          comparison = a.category.localeCompare(b.category);
          break;
        default:
          comparison = 0;
      }
      
      return sortOrder === 'asc' ? comparison : -comparison;
    });

    return filtered;
  }, [transactions, filters, sortBy, sortOrder, t]);

  // Get unique months from transactions
  const availableMonths = useMemo(() => {
    const months = [...new Set(transactions.map(t => t.date.substring(0, 7)))];
    return months.sort().reverse();
  }, [transactions]);

  // Calculate totals for filtered transactions
  const totals = useMemo(() => {
    const income = filteredTransactions
      .filter(t => t.type === 'income')
      .reduce((sum, t) => sum + parseFloat(t.amount), 0);
    
    const expenses = filteredTransactions
      .filter(t => t.type === 'expense')
      .reduce((sum, t) => sum + parseFloat(t.amount), 0);
    
    return { income, expenses, balance: income - expenses };
  }, [filteredTransactions]);

  const handleDelete = (id) => {
    if (window.confirm(isRTL ? 'هل تريد حذف هذه المعاملة؟' : 'Are you sure you want to delete this transaction?')) {
      deleteTransaction(id);
    }
  };

  const exportToCSV = () => {
    const headers = [
      'Date',
      'Type',
      'Category',
      'Description',
      'Amount',
      'Currency'
    ];

    const csvData = filteredTransactions.map(t => [
      t.date,
      t.type,
      t.category,
      t.description || '',
      t.amount,
      t.currency || currency
    ]);

    const csvContent = [headers, ...csvData]
      .map(row => row.map(field => `"${field}"`).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `budget_transactions_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="space-y-6 slide-in">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">{t('nav.transactions')}</h2>
          <p className="text-gray-600">
            {filteredTransactions.length} {isRTL ? 'معاملة' : 'transactions'}
          </p>
        </div>
        <button
          onClick={exportToCSV}
          className="btn-primary flex items-center space-x-2 rtl:space-x-reverse"
          disabled={filteredTransactions.length === 0}
        >
          <span>📊</span>
          <span>{t('buttons.export')} CSV</span>
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="card bg-gradient-to-r from-green-400 to-green-600 text-white text-center">
          <h3 className="font-semibold">{t('dashboard.total_income')}</h3>
          <p className="text-xl font-bold">{formatCurrency(totals.income)}</p>
        </div>
        <div className="card bg-gradient-to-r from-red-400 to-red-600 text-white text-center">
          <h3 className="font-semibold">{t('dashboard.total_expenses')}</h3>
          <p className="text-xl font-bold">{formatCurrency(totals.expenses)}</p>
        </div>
        <div className={`card text-white text-center ${
          totals.balance >= 0 
            ? 'bg-gradient-to-r from-blue-400 to-blue-600' 
            : 'bg-gradient-to-r from-orange-400 to-orange-600'
        }`}>
          <h3 className="font-semibold">{t('dashboard.balance')}</h3>
          <p className="text-xl font-bold">{formatCurrency(totals.balance)}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="card">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">{t('buttons.filter')}</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
          {/* Type Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {isRTL ? 'النوع' : 'Type'}
            </label>
            <select
              value={filters.type}
              onChange={(e) => setFilters(prev => ({ ...prev, type: e.target.value }))}
              className="form-input"
            >
              <option value="all">{isRTL ? 'الكل' : 'All'}</option>
              <option value="income">{t('transaction.income')}</option>
              <option value="expense">{t('transaction.expense')}</option>
            </select>
          </div>

          {/* Category Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('transaction.category')}
            </label>
            <select
              value={filters.category}
              onChange={(e) => setFilters(prev => ({ ...prev, category: e.target.value }))}
              className="form-input"
            >
              <option value="all">{isRTL ? 'الكل' : 'All'}</option>
              {categories.map(cat => (
                <option key={cat} value={cat}>
                  {t(`categories.${cat}`)}
                </option>
              ))}
            </select>
          </div>

          {/* Month Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {isRTL ? 'الشهر' : 'Month'}
            </label>
            <select
              value={filters.month}
              onChange={(e) => setFilters(prev => ({ ...prev, month: e.target.value }))}
              className="form-input"
            >
              <option value="all">{isRTL ? 'الكل' : 'All'}</option>
              {availableMonths.map(month => (
                <option key={month} value={month}>
                  {new Date(month + '-01').toLocaleDateString(isRTL ? 'ar-YE' : 'en-US', { 
                    year: 'numeric', 
                    month: 'long' 
                  })}
                </option>
              ))}
            </select>
          </div>

          {/* Sort */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {isRTL ? 'الترتيب' : 'Sort by'}
            </label>
            <div className="flex gap-2">
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="form-input flex-1"
              >
                <option value="date">{t('transaction.date')}</option>
                <option value="amount">{t('transaction.amount')}</option>
                <option value="category">{t('transaction.category')}</option>
              </select>
              <button
                onClick={() => setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc')}
                className="btn-secondary px-3"
              >
                {sortOrder === 'asc' ? '↑' : '↓'}
              </button>
            </div>
          </div>
        </div>

        {/* Search */}
        <div>
          <input
            type="text"
            value={filters.search}
            onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
            className="form-input"
            placeholder={isRTL ? 'البحث في الوصف أو الفئة...' : 'Search description or category...'}
          />
        </div>
      </div>

      {/* Transactions List */}
      <div className="space-y-3">
        {filteredTransactions.length > 0 ? (
          filteredTransactions.map(transaction => (
            <div
              key={transaction.id}
              className={`transaction-item ${
                transaction.type === 'income' ? 'transaction-income' : 'transaction-expense'
              } hover:shadow-lg`}
            >
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 rtl:space-x-reverse mb-2">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center text-xl ${
                      transaction.type === 'income' 
                        ? 'bg-green-100 text-green-600' 
                        : 'bg-red-100 text-red-600'
                    }`}>
                      {transaction.type === 'income' ? '💰' : '💸'}
                    </div>
                    <div>
                      <p className="font-semibold text-gray-800">
                        {transaction.description || t(`categories.${transaction.category}`)}
                      </p>
                      <p className="text-sm text-gray-600">
                        {t(`categories.${transaction.category}`)}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4 rtl:space-x-reverse text-sm text-gray-500">
                    <span>📅 {transaction.date}</span>
                    <span>💱 {transaction.currency || currency}</span>
                  </div>
                </div>
                
                <div className="text-right rtl:text-left">
                  <p className={`text-xl font-bold mb-1 ${
                    transaction.type === 'income' ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {transaction.type === 'income' ? '+' : '-'}{formatCurrency(transaction.amount)}
                  </p>
                  <button
                    onClick={() => handleDelete(transaction.id)}
                    className="text-red-500 hover:text-red-700 transition-colors duration-200"
                  >
                    🗑️ {t('buttons.delete')}
                  </button>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="card text-center py-12">
            <div className="text-6xl mb-4">📝</div>
            <p className="text-gray-500 mb-4">
              {isRTL ? 'لا توجد معاملات تطابق المرشحات المحددة' : 'No transactions match your filters'}
            </p>
            <button
              onClick={() => setFilters({ type: 'all', category: 'all', month: 'all', search: '' })}
              className="btn-secondary"
            >
              {isRTL ? 'إعادة تعيين المرشحات' : 'Reset Filters'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default TransactionList;